/* eslint-disable react/jsx-curly-newline */
import React, { useMemo, useState } from 'react';
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetOverlay } from '@frontend/shadcn/components/ui/sheet';
import { CalendarIcon, XmarkIcon, PencilIcon, CheckIcon, BrowserIcon } from '@revfluence/fresh-icons/solid/esm';
import { Button, buttonVariants } from '@frontend/shadcn/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@frontend/shadcn/components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@frontend/shadcn/components/ui/accordion';
import dayjs from 'dayjs';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@frontend/shadcn/components/ui/tabs';
import { Badge } from '@frontend/shadcn/components/ui/badge';
import {
  ArrowUpRightFromSquareIcon,
  ChartBarIcon,
  ChevronRightIcon,
  InfoIcon,
  MegaphoneIcon,
  PlusIcon,
} from '@revfluence/fresh-icons/regular/esm';
import { Input } from '@frontend/shadcn/components/ui/input';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '@frontend/shadcn/components/ui/form';
import { COUNTRY_DATA } from '@frontend/app/constants/countryData';
import { MultiSelect } from '@frontend/app/components/MultiSelect/MultiSelect';
import { LayersIcon } from '@radix-ui/react-icons';
import { FieldErrors, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { logger } from '@common';
import { MetaIcon } from '@revfluence/fresh-icons/brands/esm';
import { cn } from '@frontend/shadcn/lib/utils';
import { FolderBookmarkIcon, Grid2Icon } from '@revfluence/fresh-icons/solid';
import { Card } from '@frontend/shadcn/components/ui/card';
import { Link } from 'react-router-dom';
import { SOCIAL_POST_APP_ID } from '@frontend/app/constants/applicationIds';
import { PlatformIcon, PostTypeLabel } from './components/content-preview-header';
import {
  CreatorAvatar,
  CreatorInfo,
  CreatorActionDownload,
  CreatorActionShare,
  CreatorActionsContainer,
  CreatorContainer,
  CreatorAvatarContainer,
} from './components/content-preview-creator';
import ContentPreviewContent from './components/content-preview-content';
import { MetricCard, MetricCardLabel, MetricCardValue } from './components/metric-card';
import { PerformanceHeader, PerformanceTitle, PerformanceDateRange } from './components/performance-header';
import {
  SheetGrid,
  SheetTopBar,
  SheetColumn,
  PreviewSheetContent,
  PreviewSheetHeading,
  PreviewSheetCaption,
  PreviewSheetLabel,
  PreviewSheetSection,
  PreviewSheetBadgeContainer,
  PreviewSheetBadge,
  PreviewSheetEmpty,
  MetricsGrid,
  SheetHeader,
  SheetTitle,
} from './components/content-preview-sheet';

import ContentPreviewAlert, { IAlertProps } from './components/content-preview-alert';

import { boostPostSchema } from './types/boost-post-schema';
import type { BoostPostFormValues } from './types/boost-post-schema';

import {
  type ContentPreviewData,
  type ContentPreviewSheetProps as BaseContentPreviewSheetProps,
  MetaAd,
} from './types';

const compactFormatter = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
});

interface ContentPreviewSheetProps extends Omit<BaseContentPreviewSheetProps, 'content'> {
  content: ContentPreviewData & {
    onEditProject?: () => void;
    onEditGroup?: () => void;
    onEditMusic?: () => void;
    onEditRetailer?: () => void;
    onEditBrands?: () => void;
  };
}

const useBoost = (content: ContentPreviewData, availableMetaAdAccounts?: { id: string }[]) => {
  const boostPostForm = useForm<BoostPostFormValues>({
    resolver: zodResolver(boostPostSchema),
    defaultValues: {
      campaign: { type: 'existing' },
      adSet: { type: 'existing' },
      post: {
        postExternalId: content.postExternalId,
        platform: content.metadata.platform === 'instagram' ? 'instagram' : undefined,
      },
      // ...other default values
    },
    mode: 'onTouched',
  });
  const campaignType = boostPostForm.watch('campaign.type');
  const campaignId = boostPostForm.watch('campaign.campaignId');
  const campaignName = boostPostForm.watch('campaign.name');
  const campaignObjective = boostPostForm.watch('campaign.objective');

  // Set default ad account to the first available
  React.useEffect(() => {
    if (availableMetaAdAccounts && availableMetaAdAccounts.length > 0 && !boostPostForm.getValues('adAccount.id')) {
      boostPostForm.setValue('adAccount.id', availableMetaAdAccounts[0].id);
    }
  }, [availableMetaAdAccounts, boostPostForm]);

  const { formState } = boostPostForm;
  const { errors } = formState;
  const isCampaignValid =
    campaignType === 'existing'
      ? !!campaignId && !errors.campaign
      : !!campaignName && !!campaignObjective && !errors.campaign;

  return { boostPostForm, isCampaignValid };
};

const CAMPAIGN_OBJECTIVES = [
  { value: 'OUTCOME_APP_PROMOTION', label: 'App Promotion' },
  { value: 'OUTCOME_AWARENESS', label: 'Awareness' },
  { value: 'OUTCOME_ENGAGEMENT', label: 'Engagement' },
  { value: 'OUTCOME_LEADS', label: 'Leads' },
  { value: 'OUTCOME_SALES', label: 'Sales' },
  { value: 'OUTCOME_TRAFFIC', label: 'Traffic' },
];

function contentPreviewAlertProps(
  advertisableMediaPermissions?: ContentPreviewData['advertisableMediaPermissions'],
): IAlertProps {
  const isAuthorized = advertisableMediaPermissions?.some((permission) => permission.hasPermissions);

  if (isAuthorized) {
    return {
      type: 'success' as const,
      children: <span>You are authorized to boost this post on Instagram</span>,
    };
  }

  return {
    type: 'unAuthorized' as const,
    children: (
      <span>
        You are not authorized to boost this post on Instagram.
        <br />
        If you proceed to boost the post, we can attempt to contact the creator on your behalf to obtain the necessary
        permission and then proceed with your ad creation request.
      </span>
    ),
  };
}

export function ContentPreviewSheetBoost({
  content,
  open,
  onOpenChange,
  availableProjects,
  availableGroups,
  availableMetaAdAccounts,
  children,
  onBoostPost,
}: Readonly<ContentPreviewSheetProps>) {
  const { boostPostForm, isCampaignValid } = useBoost(content, availableMetaAdAccounts);
  const [boostView, setBoostView] = useState<'view' | 'create'>('view');
  const [selectedBudgetType, setSelectedBudgetType] = useState<'lifetime' | 'daily'>('daily');
  const campaignType = boostPostForm.watch('campaign.type');
  const adSetType = boostPostForm.watch('adSet.type');
  const selectedAdAccountId = boostPostForm.watch('adAccount.id');
  const selectedCampaignId = boostPostForm.watch('campaign.campaignId');
  const selectedAdAccount = availableMetaAdAccounts?.find((metaAdAccount) => metaAdAccount.id === selectedAdAccountId);
  const availableAdCampaigns = selectedAdAccount?.campaigns;
  const selectedAdCampaign = availableAdCampaigns?.find((adCampaign) => adCampaign.id === selectedCampaignId);
  const availableAdSets = selectedAdCampaign?.adSets;

  const onBoostPostSubmit = (values: BoostPostFormValues) => {
    logger.info('Boost Post Form Submitted:', values);
    // handle submit
    onBoostPost?.(values, (name: string, value: any) => {
      boostPostForm.setValue(name as any, value);
    });
  };

  const onBoostPostError = (errors: FieldErrors<BoostPostFormValues>) => {
    logger.error('Boost Post Form Errors:', errors);
  };

  const onBoostPostCancel = () => {
    logger.info('Boost Post Form Canceled');
    boostPostForm.reset();
    setBoostView('view');
  };

  const metaAdAccountOptions =
    availableMetaAdAccounts?.map((metaAdAccount: { id: string; name: string }) => ({
      label: metaAdAccount.name,
      value: metaAdAccount.id,
    })) ?? [];

  const campaignOptions =
    availableAdCampaigns?.map((adCampaign: { name: string; id: string }) => ({
      label: adCampaign.name,
      value: adCampaign.id,
    })) ?? [];

  const projectOptions =
    availableProjects?.map((p: { name: string; id: string }) => ({
      label: p.name,
      value: p.id,
    })) ?? [];

  const groupOptions =
    availableGroups?.map((g: { name: string; id: string }) => ({
      label: g.name,
      value: g.id,
    })) ?? [];

  const currentAds = useMemo(() => {
    const currentAds: MetaAd[] = [];
    availableMetaAdAccounts?.forEach((adAccount) => {
      adAccount.campaigns?.forEach((campaign) => {
        campaign.adSets?.forEach((adSet) => {
          adSet.ads.forEach((ad) => {
            ad.creatives.forEach((creative) => {
              if (creative.metaAdCreative.sourceInstagramMediaId === content.postExternalId) {
                currentAds.push({
                  ...ad,
                  campaign,
                  adSet,
                });
              }
            });
          });
        });
      });
    });
    return currentAds;
  }, [availableMetaAdAccounts, content.postExternalId]);

  const viewMode = useMemo(() => {
    if (!availableMetaAdAccounts || availableMetaAdAccounts.length === 0) {
      return 'viewNoAdAccounts';
    }
    if (currentAds?.length > 0) {
      return 'viewCurrent';
    }
    return 'viewNoAds';
  }, [currentAds, availableMetaAdAccounts]);
  console.log('currentAds', currentAds);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {children && <SheetTrigger asChild>{children}</SheetTrigger>}
      <SheetOverlay className="z-[190]" />
      <SheetContent
        className="w-full overflow-y-auto p-0 z-[200] sm:max-w-[1000px] [&>button:first-of-type]:hidden flex flex-col"
        side="right"
      >
        <div className="flex flex-col h-full flex-1">
          <SheetHeader>
            <SheetTitle>Content Preview</SheetTitle>
            <SheetClose asChild>
              <Button variant="ghost" size="icon" aria-label="Close preview" onClick={() => onOpenChange(false)}>
                <XmarkIcon className="h-4 w-4" />
              </Button>
            </SheetClose>
          </SheetHeader>

          <SheetTopBar>
            <div className="flex items-center gap-2">
              <PlatformIcon platform={content.metadata.platform} />
              <PostTypeLabel postType={content.metadata.postType} />
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="flex items-center gap-2 rounded-lg">
                <CalendarIcon className="h-3 w-3" />
                <span>
                  {new Date(content.createdAt).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
              </Badge>
            </div>
          </SheetTopBar>

          <SheetGrid>
            {/* Left column - Creator, Content and Caption */}
            <SheetColumn>
              <PreviewSheetContent>
                <CreatorContainer>
                  <CreatorAvatarContainer>
                    <CreatorAvatar name={content.creator.name} avatarUrl={content.creator.avatarUrl} />
                    <CreatorInfo name={content.creator.name} handle={content.creator.handle} />
                  </CreatorAvatarContainer>
                  <CreatorActionsContainer>
                    <CreatorActionDownload onClick={content.onDownload} />
                    <CreatorActionShare
                      onClick={() => {
                        if (content.onShare) {
                          content.onShare();
                        } else {
                          window.open(window.location.href, '_blank', 'noopener,noreferrer');
                        }
                      }}
                    />
                  </CreatorActionsContainer>
                </CreatorContainer>

                <ContentPreviewContent
                  mediaUrl={content.mediaUrl}
                  contentType={content.metadata.contentType}
                  originalUrl={content.originalUrl}
                />

                <div>
                  <PreviewSheetHeading>Post Caption</PreviewSheetHeading>
                  <PreviewSheetCaption caption={content.caption} />
                </div>
              </PreviewSheetContent>
            </SheetColumn>

            {/* Right column - Tabs, Metrics and Details */}
            <SheetColumn>
              <Tabs defaultValue="overview" className="w-full h-full flex flex-col">
                <div className="border-b mt-3">
                  <TabsList className="border-0 bg-transparent m-0">
                    <TabsTrigger
                      value="overview"
                      className="relative data-[state=active]:text-gray-900 data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-gray-900 rounded-none bg-transparent data-[state=active]:bg-transparent"
                    >
                      Overview
                    </TabsTrigger>
                    <TabsTrigger
                      value="boost"
                      className="relative data-[state=active]:text-gray-900 data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-gray-900 rounded-none bg-transparent data-[state=active]:bg-transparent"
                    >
                      Boost
                    </TabsTrigger>
                  </TabsList>
                </div>
                <TabsContent value="overview" className="mt-4">
                  <Accordion type="multiple" defaultValue={['performance', 'info', 'additional']} className="space-y-4">
                    <AccordionItem value="performance" className="">
                      <AccordionTrigger className="p-0 hover:no-underline">
                        <PerformanceHeader className="w-full">
                          <PerformanceTitle>Post Performance</PerformanceTitle>
                          <PerformanceDateRange>
                            {content.metrics.metadata.startDate && content.metrics.metadata.endDate && (
                              <>
                                {dayjs(content.metrics.metadata.startDate).format('MMM D, YYYY')} -{' '}
                                {dayjs(content.metrics.metadata.endDate).format('MMM D, YYYY')}
                              </>
                            )}
                          </PerformanceDateRange>
                        </PerformanceHeader>
                      </AccordionTrigger>
                      <AccordionContent className="pt-4">
                        <MetricsGrid>
                          <MetricCard className="border-[#4F46E5]">
                            <MetricCardLabel>Impressions</MetricCardLabel>
                            <MetricCardValue>{compactFormatter.format(content.metrics.impressions)}</MetricCardValue>
                          </MetricCard>

                          <MetricCard className="border-[#E11D48]">
                            <MetricCardLabel>Engagement</MetricCardLabel>
                            <MetricCardValue>{compactFormatter.format(content.metrics.engagement)}</MetricCardValue>
                          </MetricCard>

                          <MetricCard className="border-[#EC4899]">
                            <MetricCardLabel>Engagement %</MetricCardLabel>
                            <MetricCardValue>{content.metrics.engagementRate.toFixed(1)}%</MetricCardValue>
                          </MetricCard>

                          <MetricCard className="border-[#059669]">
                            <MetricCardLabel>TMV</MetricCardLabel>
                            <MetricCardValue>{currencyFormatter.format(content.metrics.tmv ?? 0)}</MetricCardValue>
                          </MetricCard>
                        </MetricsGrid>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="info" className="">
                      <AccordionTrigger className="p-0 hover:no-underline">
                        <h3 className="text-md font-medium">Info</h3>
                      </AccordionTrigger>
                      <AccordionContent className="pt-4">
                        <div className="space-y-4">
                          <PreviewSheetSection>
                            <PreviewSheetLabel>Project</PreviewSheetLabel>
                            <PreviewSheetBadgeContainer>
                              {content.projectEditingState?.isEditing ? (
                                <div className="flex items-center gap-2 w-full">
                                  <Select
                                    value={content.projectEditingState.projectId?.toString() ?? ''}
                                    onValueChange={(value) => {
                                      if (availableProjects) {
                                        const selectedProject = availableProjects.find(
                                          (p) => p.id.toString() === value,
                                        );
                                        if (selectedProject && content.projectEditingState) {
                                          // Just update the state, don't save yet
                                          content.projectEditingState.updateState?.({
                                            projectId: selectedProject.id.toString(),
                                            projectName: selectedProject.name,
                                          });
                                        }
                                      }
                                    }}
                                  >
                                    <SelectTrigger className="h-8">
                                      <SelectValue placeholder="Select a project" />
                                    </SelectTrigger>
                                    <SelectContent className="z-[1000]">
                                      {projectOptions.map((project) => (
                                        <SelectItem key={project.value} value={project.value}>
                                          {project.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6"
                                    onClick={() => {
                                      if (
                                        content.projectEditingState?.onSave &&
                                        content.projectEditingState.projectId &&
                                        content.projectEditingState.projectName
                                      ) {
                                        content.projectEditingState.onSave(
                                          content.projectEditingState.projectId,
                                          content.projectEditingState.projectName,
                                        );
                                      }
                                    }}
                                  >
                                    <CheckIcon className="h-4 w-4 text-green-600" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6"
                                    onClick={() => {
                                      if (content.projectEditingState?.onCancel) {
                                        content.projectEditingState.onCancel();
                                      }
                                    }}
                                  >
                                    <XmarkIcon className="h-4 w-4 text-red-600" />
                                  </Button>
                                </div>
                              ) : (
                                <>
                                  {content.metadata.project ? (
                                    <PreviewSheetBadge className="bg-blue-500 hover:bg-blue-600">
                                      {content.metadata.project.name}
                                    </PreviewSheetBadge>
                                  ) : (
                                    <PreviewSheetEmpty />
                                  )}
                                  {content.onEditProject && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-6 w-6"
                                      onClick={content.onEditProject}
                                    >
                                      <PencilIcon className="h-4 w-4 text-gray-900" />
                                    </Button>
                                  )}
                                </>
                              )}
                            </PreviewSheetBadgeContainer>
                          </PreviewSheetSection>

                          <PreviewSheetSection>
                            <PreviewSheetLabel>Group</PreviewSheetLabel>
                            <PreviewSheetBadgeContainer>
                              {content.groupEditingState?.isEditing ? (
                                <div className="flex items-center gap-2 w-full">
                                  <Select
                                    value={content.groupEditingState.groupId?.toString() ?? ''}
                                    onValueChange={(value) => {
                                      if (availableGroups) {
                                        const selectedGroup = availableGroups.find((g) => g.id.toString() === value);
                                        if (selectedGroup && content.groupEditingState) {
                                          // Just update the state, don't save yet
                                          content.groupEditingState.updateState?.({
                                            groupId: selectedGroup.id.toString(),
                                            groupName: selectedGroup.name,
                                          });
                                        }
                                      }
                                    }}
                                  >
                                    <SelectTrigger className="h-8">
                                      <SelectValue placeholder="Select a group" />
                                    </SelectTrigger>
                                    <SelectContent className="z-[1000]">
                                      {groupOptions.map((group) => (
                                        <SelectItem key={group.value} value={group.value}>
                                          {group.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6"
                                    onClick={() => {
                                      if (
                                        content.groupEditingState?.onSave &&
                                        content.groupEditingState.groupId &&
                                        content.groupEditingState.groupName
                                      ) {
                                        content.groupEditingState.onSave(
                                          content.groupEditingState.groupId,
                                          content.groupEditingState.groupName,
                                        );
                                      }
                                    }}
                                  >
                                    <CheckIcon className="h-4 w-4 text-green-600" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6"
                                    onClick={content.groupEditingState?.onCancel}
                                  >
                                    <XmarkIcon className="h-4 w-4 text-red-600" />
                                  </Button>
                                </div>
                              ) : (
                                <>
                                  {content.metadata.group ? (
                                    <PreviewSheetBadge className="bg-pink-500 hover:bg-pink-600">
                                      {content.metadata.group.name}
                                    </PreviewSheetBadge>
                                  ) : (
                                    <PreviewSheetEmpty />
                                  )}
                                  {content.onEditGroup && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-6 w-6"
                                      onClick={content.onEditGroup}
                                    >
                                      <PencilIcon className="h-4 w-4 text-gray-900" />
                                    </Button>
                                  )}
                                </>
                              )}
                            </PreviewSheetBadgeContainer>
                          </PreviewSheetSection>

                          <PreviewSheetSection>
                            <PreviewSheetLabel>Last Updated Date</PreviewSheetLabel>
                            <div className="text-sm">
                              {new Date(content.updatedAt || content.createdAt).toLocaleDateString()}
                            </div>
                          </PreviewSheetSection>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </TabsContent>
                <TabsContent value="boost" className="mt-4 flex flex-col h-full">
                  {boostView === 'view' && (
                    <div className="flex flex-col flex-1 gap-4">
                      <ContentPreviewAlert {...contentPreviewAlertProps(content.advertisableMediaPermissions)} />
                      {viewMode === 'viewNoAds' && (
                        <>
                          <div>
                            <h3 className="text-md font-medium text-gray-900">
                              Create Partnership Ads by Boosting an existing post
                            </h3>
                            <p className="text-sm text-gray-600 mb-0">
                              Boost creator posts as Meta ads to reach wider audiences using branded content.
                            </p>
                          </div>
                        </>
                      )}
                      {viewMode === 'viewNoAdAccounts' && (
                        <div className="space-y-4">
                          <div className="flex flex-col gap-2">
                            <div>
                              <h3 className="text-md font-medium text-gray-900">No Ad Accounts Found</h3>
                              <p className="text-sm text-gray-600 mb-0">
                                To continue, please connect at least one Meta Ads account. Add your account here.
                              </p>
                            </div>
                            <div>
                              <Link to={`/settings/${SOCIAL_POST_APP_ID}`}>
                                <Button variant="primary" size="sm" className="flex items-center gap-2">
                                  <MetaIcon className="h-4 w-4 text-white" />
                                  Add Ad Account
                                </Button>
                              </Link>
                            </div>
                          </div>
                        </div>
                      )}
                      {viewMode === 'viewCurrent' && (
                        <div className="space-y-4">
                          <h3 className="text-md font-medium text-gray-900">Boost Post</h3>
                          <div className="space-y-3">
                            {currentAds.map((ad) => (
                              <Card key={ad.id} className=" px-4 py-3 flex flex-col gap-3">
                                <div className="flex flex-col gap-2">
                                  <div className="flex items-center justify-between gap-2">
                                    <Badge className="px-2 py-0.5 text-xs rounded-lg bg-green-4 text-white flex gap-2">
                                      <MetaIcon className="h-3 w-3 text-white" />
                                      {ad.status}
                                    </Badge>
                                    {ad?.adSet?.stopTime ? (
                                      <span className="text-sm font-normal text-grey-5">
                                        Valid till {dayjs(ad?.adSet?.stopTime).format('MMM D, YYYY')}
                                      </span>
                                    ) : (
                                      <span className="text-sm font-normal text-grey-5">Ongoing</span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <BrowserIcon className="h-3.5 w-3.5 text-teal-4" />
                                    <span className="text-sm font-medium text-grey-6">{ad.name}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <FolderBookmarkIcon className="h-3.5 w-3.5 text-blue-5" />
                                    <span className="text-sm font-normal text-grey-5">{ad?.adSet?.name}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Grid2Icon className="h-3.5 w-3.5 text-lime-5" />
                                    <span className="text-sm font-normal text-grey-5">{ad?.campaign?.name}</span>
                                  </div>
                                </div>

                                <div className="flex gap-2">
                                  <Button size="sm" disabled variant="outline" className="flex items-center gap-2">
                                    <PencilIcon className="h-3.5 w-3.5" />
                                    Edit
                                  </Button>
                                  <Button size="sm" disabled variant="outline" className="flex items-center gap-2">
                                    <ChartBarIcon className="h-3.5 w-3.5" />
                                    Track Ad
                                  </Button>
                                  <a
                                    href={`https://adsmanager.facebook.com/adsmanager/manage/ads?act=${ad.adAccountId}&selected_campaign_ids=${ad?.campaign?.id}&selected_adset_ids=${ad?.adSet?.id}&selected_ad_ids=${ad.id}&nav_source=fb4b`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={cn(
                                      buttonVariants({ variant: 'outline', size: 'sm' }),
                                      'ml-auto',
                                      'flex items-center gap-2',
                                    )}
                                  >
                                    <ArrowUpRightFromSquareIcon className="h-3.5 w-3.5" />
                                    Open in Meta
                                  </a>
                                </div>
                              </Card>
                            ))}
                          </div>
                        </div>
                      )}
                      <div>
                        <Button
                          onClick={() => {
                            setBoostView('create');
                          }}
                          variant="primary"
                          size="sm"
                          className="flex items-center gap-2"
                        >
                          <MetaIcon className="h-4 w-4 text-white" />
                          {currentAds.length <= 0 ? <span>Boost Post</span> : <span>Create Another Boost</span>}
                        </Button>
                      </div>
                    </div>
                  )}

                  {boostView === 'create' && (
                    <Form {...boostPostForm}>
                      <form
                        className="flex flex-col flex-1"
                        onSubmit={boostPostForm.handleSubmit(onBoostPostSubmit, onBoostPostError)}
                      >
                        <div className="flex-1 space-y-4">
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="text-md font-medium text-gray-900">Boost Post</h3>
                            <FormField
                              control={boostPostForm.control}
                              name="adAccount.id"
                              render={({ field }) => (
                                <FormItem>
                                  <FormControl>
                                    <Select value={field.value} onValueChange={field.onChange}>
                                      <SelectTrigger className="w-[200px]">
                                        <SelectValue placeholder="Select Ad Account" />
                                      </SelectTrigger>
                                      <SelectContent className="z-[1000]">
                                        {metaAdAccountOptions.map((metaAdAccount) => (
                                          <SelectItem key={metaAdAccount.value} value={metaAdAccount.value}>
                                            {metaAdAccount.label}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                          <div className="rounded-lg border border-gray-200 p-4">
                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center gap-2">
                                <MegaphoneIcon className="h-4 w-4 text-gray-600" />
                                <h4 className="font-medium m-0 p-0 text-gray-900">Campaigns</h4>
                              </div>
                              {campaignType === 'new' ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="flex items-center gap-1"
                                  onClick={() => boostPostForm.setValue('campaign.type', 'existing')}
                                  type="button"
                                >
                                  <PlusIcon className="h-4 w-4" />
                                  <span>Select Existing Campaign</span>
                                </Button>
                              ) : (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="flex items-center gap-1"
                                  onClick={() => boostPostForm.setValue('campaign.type', 'new')}
                                  type="button"
                                >
                                  <PlusIcon className="h-4 w-4" />
                                  <span>New Campaign</span>
                                </Button>
                              )}
                            </div>
                            <div className="space-y-4">
                              {campaignType === 'existing' ? (
                                <FormField
                                  control={boostPostForm.control}
                                  name="campaign.campaignId"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Select Campaign</FormLabel>
                                      <FormControl>
                                        <Select
                                          value={field.value}
                                          onValueChange={field.onChange}
                                          defaultValue={field.value}
                                        >
                                          <SelectTrigger id="campaign-select" className="w-full">
                                            <SelectValue placeholder="Choose an existing campaign" />
                                          </SelectTrigger>
                                          <SelectContent className="z-[1000]">
                                            {campaignOptions.map((campaign) => (
                                              <SelectItem key={campaign.value} value={campaign.value}>
                                                {campaign.label}
                                              </SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                              ) : (
                                <>
                                  <FormField
                                    control={boostPostForm.control}
                                    name="campaign.name"
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormLabel>Campaign Name</FormLabel>
                                        <FormControl>
                                          <Input placeholder="Enter campaign name" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                  <FormField
                                    control={boostPostForm.control}
                                    name="campaign.objective"
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormLabel>Objective</FormLabel>
                                        <FormControl>
                                          <Select
                                            value={field.value}
                                            onValueChange={field.onChange}
                                            defaultValue={field.value}
                                          >
                                            <SelectTrigger id="new-campaign-objective" className="w-full">
                                              <SelectValue placeholder="Select objective" />
                                            </SelectTrigger>
                                            <SelectContent className="z-[1000]">
                                              {CAMPAIGN_OBJECTIVES.map((obj: { value: string; label: string }) => (
                                                <SelectItem key={obj.value} value={obj.value}>
                                                  {obj.label}
                                                </SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                </>
                              )}
                              <div className="rounded-lg bg-gray-50 p-4">
                                <div className="flex items-center gap-2 mb-3">
                                  <InfoIcon className="h-4 w-4 text-gray-500" />
                                  <span className="text-sm text-gray-600">
                                    Selected campaign details will appear here
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div />
                          <div className="rounded-lg border border-gray-200 p-4 flex flex-col gap-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <LayersIcon className="h-4 w-4 text-gray-600" />
                                <h4 className="font-medium m-0 p-0 text-gray-900">Ad Set</h4>
                              </div>
                              {!isCampaignValid && <ChevronRightIcon className="h-4 w-4 text-gray-600 disabled" />}
                              {isCampaignValid &&
                                (adSetType === 'existing' ? (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="flex items-center gap-1"
                                    onClick={() => boostPostForm.setValue('adSet.type', 'new')}
                                    type="button"
                                  >
                                    <PlusIcon className="h-4 w-4" />
                                    <span>New Ad Set</span>
                                  </Button>
                                ) : (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="flex items-center gap-1"
                                    onClick={() => boostPostForm.setValue('adSet.type', 'existing')}
                                    type="button"
                                  >
                                    <PlusIcon className="h-4 w-4" />
                                    <span>Select Existing Ad Set</span>
                                  </Button>
                                ))}
                            </div>
                            {isCampaignValid && (
                              <div className="space-y-4">
                                {adSetType === 'existing' ? (
                                  <FormField
                                    control={boostPostForm.control}
                                    name="adSet.id"
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormLabel>Select Ad Set</FormLabel>
                                        <FormControl>
                                          <Select
                                            value={field.value}
                                            onValueChange={(value) => {
                                              field.onChange(value);
                                              console.log('adSet.id', value);
                                            }}
                                            defaultValue={field.value}
                                          >
                                            <SelectTrigger id="adset-select" className="w-full">
                                              <SelectValue placeholder="Choose an existing ad set" />
                                            </SelectTrigger>
                                            <SelectContent className="z-[1000]">
                                              {availableAdSets?.map((adSet) => {
                                                console.log('adSet', adSet);
                                                return (
                                                  <SelectItem key={adSet.id} value={adSet.id}>
                                                    {adSet.name}
                                                  </SelectItem>
                                                );
                                              })}
                                            </SelectContent>
                                          </Select>
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                ) : (
                                  <>
                                    <FormField
                                      control={boostPostForm.control}
                                      name="adSet.name"
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormLabel>Ad Set Name</FormLabel>
                                          <FormControl>
                                            <Input placeholder="Enter ad set name" {...field} />
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />
                                    <div className="flex gap-2">
                                      <FormItem>
                                        <FormLabel>Budget Type</FormLabel>
                                        <Select
                                          onValueChange={(value: 'lifetime' | 'daily') => {
                                            if (value === 'lifetime') {
                                              boostPostForm.setValue('adSet.dailyBudget', undefined);
                                            } else {
                                              boostPostForm.setValue('adSet.lifetimeBudget', undefined);
                                            }
                                            setSelectedBudgetType(value);
                                          }}
                                          defaultValue={selectedBudgetType}
                                        >
                                          <SelectTrigger className="w-full">
                                            <SelectValue placeholder="Select budget type" />
                                          </SelectTrigger>
                                          <SelectContent className="z-[1000]">
                                            <SelectItem value="daily">Daily Budget</SelectItem>
                                            <SelectItem value="lifetime">Lifetime Budget</SelectItem>
                                          </SelectContent>
                                        </Select>
                                      </FormItem>

                                      {/* Conditionally render Lifetime Budget */}

                                      {selectedBudgetType === 'lifetime' && (
                                        <FormField
                                          control={boostPostForm.control}
                                          name="adSet.lifetimeBudget"
                                          render={({ field }) => (
                                            <FormItem>
                                              <FormLabel>Lifetime Budget</FormLabel>
                                              <FormControl>
                                                <div className="relative">
                                                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                    <span className="text-gray-500 sm:text-sm">$</span>
                                                  </div>
                                                  <Input
                                                    type="number"
                                                    placeholder="0.00"
                                                    min="0"
                                                    step="0.01"
                                                    className="pl-7"
                                                    value={field.value ?? ''}
                                                    onChange={(e) => {
                                                      const val = e.target.value;
                                                      field.onChange(val === '' ? undefined : parseFloat(val));
                                                      if (val !== '') {
                                                        boostPostForm.setValue('adSet.dailyBudget', undefined);
                                                      }
                                                    }}
                                                  />
                                                </div>
                                              </FormControl>
                                              <FormMessage />
                                            </FormItem>
                                          )}
                                        />
                                      )}

                                      {/* Conditionally render Daily Budget */}
                                      {selectedBudgetType === 'daily' && (
                                        <FormField
                                          control={boostPostForm.control}
                                          name="adSet.dailyBudget"
                                          render={({ field }) => (
                                            <FormItem>
                                              <FormLabel>Daily Budget</FormLabel>
                                              <FormControl>
                                                <div className="relative">
                                                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                    <span className="text-gray-500 sm:text-sm">$</span>
                                                  </div>
                                                  <Input
                                                    type="number"
                                                    placeholder="0.00"
                                                    min="0"
                                                    step="0.01"
                                                    className="pl-7"
                                                    value={field.value ?? ''}
                                                    onChange={(e) => {
                                                      const val = e.target.value;
                                                      field.onChange(val === '' ? undefined : parseFloat(val));
                                                      if (val !== '') {
                                                        boostPostForm.setValue('adSet.lifetimeBudget', undefined);
                                                      }
                                                    }}
                                                  />
                                                </div>
                                              </FormControl>
                                              <FormMessage />
                                            </FormItem>
                                          )}
                                        />
                                      )}
                                    </div>
                                    <div>
                                      <FormField
                                        control={boostPostForm.control}
                                        name="adSet.startTime"
                                        render={({ field }) => (
                                          <FormItem>
                                            <FormLabel>Start Time</FormLabel>
                                            <FormControl>
                                              <Input
                                                type="datetime-local"
                                                value={
                                                  field.value ? new Date(field.value).toISOString().slice(0, 16) : ''
                                                }
                                                onChange={(e) =>
                                                  field.onChange(e.target.value ? new Date(e.target.value) : undefined)
                                                }
                                              />
                                            </FormControl>
                                            <FormMessage />
                                          </FormItem>
                                        )}
                                      />
                                      <FormField
                                        control={boostPostForm.control}
                                        name="adSet.stopTime"
                                        render={({ field }) => (
                                          <FormItem>
                                            <FormLabel>Stop Time</FormLabel>
                                            <FormControl>
                                              <Input
                                                type="datetime-local"
                                                value={
                                                  field.value ? new Date(field.value).toISOString().slice(0, 16) : ''
                                                }
                                                onChange={(e) =>
                                                  field.onChange(e.target.value ? new Date(e.target.value) : undefined)
                                                }
                                              />
                                            </FormControl>
                                            <FormMessage />
                                          </FormItem>
                                        )}
                                      />
                                    </div>
                                    <FormField
                                      control={boostPostForm.control}
                                      name="adSet.billingEvent"
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormLabel>Billing Event</FormLabel>
                                          <FormControl>
                                            <Select
                                              value={field.value}
                                              onValueChange={field.onChange}
                                              defaultValue={field.value}
                                            >
                                              <SelectTrigger className="w-full">
                                                <SelectValue placeholder="Select billing event" />
                                              </SelectTrigger>
                                              <SelectContent className="z-[1000]">
                                                {[
                                                  'APP_INSTALLS',
                                                  'CLICKS',
                                                  'IMPRESSIONS',
                                                  'LINK_CLICKS',
                                                  'LISTING_INTERACTION',
                                                  'OFFER_CLAIMS',
                                                  'PAGE_LIKES',
                                                  'POST_ENGAGEMENT',
                                                  'PURCHASE',
                                                  'THRUPLAY',
                                                  'VIDEO_VIEWS',
                                                ].map((event) => (
                                                  <SelectItem key={event} value={event}>
                                                    {event
                                                      .replace(/_/g, ' ')
                                                      .toLowerCase()
                                                      .replace(/\b\w/g, (c) => c.toUpperCase())}
                                                  </SelectItem>
                                                ))}
                                              </SelectContent>
                                            </Select>
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />

                                    <FormField
                                      control={boostPostForm.control}
                                      name="adSet.bidAmount"
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormLabel>Bid Amount</FormLabel>
                                          <FormControl>
                                            <div className="relative">
                                              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                <span className="text-gray-500 sm:text-sm">$</span>
                                              </div>
                                              <Input
                                                type="number"
                                                placeholder="0.00"
                                                min="0"
                                                step="0.01"
                                                className="pl-7"
                                                {...field}
                                                value={field.value ?? ''}
                                                onChange={(e) => {
                                                  const val = e.target.value;
                                                  field.onChange(val === '' ? undefined : parseFloat(val));
                                                }}
                                              />
                                            </div>
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />
                                    <Accordion type="single" collapsible className="w-full">
                                      <AccordionItem value="more-adset-options">
                                        <AccordionTrigger>Show More Options</AccordionTrigger>
                                        <AccordionContent className="space-y-4 pt-4">
                                          <FormField
                                            control={boostPostForm.control}
                                            name="adSet.targeting.geo_locations.countries"
                                            render={({ field }) => (
                                              <FormItem>
                                                <FormLabel>Target Countries</FormLabel>
                                                <FormControl>
                                                  <MultiSelect
                                                    value={field.value || []}
                                                    onChange={field.onChange}
                                                    options={COUNTRY_DATA.map((country) => ({
                                                      label: country.countryName,
                                                      value: country.countryShortCode,
                                                    }))}
                                                    placeholder="Select countries"
                                                  />
                                                </FormControl>
                                                <FormMessage />
                                              </FormItem>
                                            )}
                                          />

                                          <FormField
                                            control={boostPostForm.control}
                                            name="adSet.status"
                                            render={({ field }) => (
                                              <FormItem>
                                                <FormLabel>Status</FormLabel>
                                                <FormControl>
                                                  <Select
                                                    value={field.value}
                                                    onValueChange={field.onChange}
                                                    defaultValue={field.value}
                                                  >
                                                    <SelectTrigger className="w-full">
                                                      <SelectValue placeholder="Select status" />
                                                    </SelectTrigger>
                                                    <SelectContent className="z-[1000]">
                                                      {[
                                                        'ACTIVE',
                                                        'PAUSED',
                                                        'ARCHIVED',
                                                        'DELETED',
                                                        'IN_PROCESS',
                                                        'PENDING_REVIEW',
                                                        'DISAPPROVED',
                                                      ].map((status) => (
                                                        <SelectItem key={status} value={status}>
                                                          {status
                                                            .replace(/_/g, ' ')
                                                            .toLowerCase()
                                                            .replace(/\b\w/g, (c) => c.toUpperCase())}
                                                        </SelectItem>
                                                      ))}
                                                    </SelectContent>
                                                  </Select>
                                                </FormControl>
                                                <FormMessage />
                                              </FormItem>
                                            )}
                                          />
                                        </AccordionContent>
                                      </AccordionItem>
                                    </Accordion>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="rounded-lg border border-gray-200 p-4 flex flex-col gap-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <LayersIcon className="h-4 w-4 text-gray-600" />
                                <h4 className="font-medium m-0 p-0 text-gray-900">Configure Ad</h4>
                              </div>
                              {!isCampaignValid && <ChevronRightIcon className="h-4 w-4 text-gray-600 disabled" />}
                            </div>
                            {isCampaignValid && (
                              <div className="space-y-4">
                                <FormField
                                  control={boostPostForm.control}
                                  name="ad.name"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Ad Name</FormLabel>
                                      <FormControl>
                                        <Input placeholder="Enter ad name" {...field} />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <FormField
                                  control={boostPostForm.control}
                                  name="ad.callToActionType"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Call to Action</FormLabel>
                                      <FormControl>
                                        <Select
                                          value={field.value}
                                          onValueChange={field.onChange}
                                          defaultValue={field.value}
                                        >
                                          <SelectTrigger id="adset-action" className="w-full">
                                            <SelectValue placeholder="Choose a call to action" />
                                          </SelectTrigger>
                                          <SelectContent className="z-[1000]">
                                            {[
                                              'ADD_TO_CART',
                                              'APPLY_NOW',
                                              'ASK_ABOUT_SERVICES',
                                              'ASK_FOR_MORE_INFO',
                                              'AUDIO_CALL',
                                              'BOOK_A_CONSULTATION',
                                              'BOOK_NOW',
                                              'BOOK_TRAVEL',
                                              'BUY',
                                              'BUY_NOW',
                                              'BUY_TICKETS',
                                              'BUY_VIA_MESSAGE',
                                              'CALL',
                                              'CALL_ME',
                                              'CALL_NOW',
                                              'CHAT_WITH_US',
                                              'CONFIRM',
                                              'CONTACT',
                                              'CONTACT_US',
                                              'DONATE',
                                              'DONATE_NOW',
                                              'DOWNLOAD',
                                              'EVENT_RSVP',
                                              'FIND_A_GROUP',
                                              'FIND_YOUR_GROUPS',
                                              'FOLLOW_NEWS_STORYLINE',
                                              'FOLLOW_PAGE',
                                              'FOLLOW_USER',
                                              'GET_A_QUOTE',
                                              'GET_DIRECTIONS',
                                              'GET_IN_TOUCH',
                                              'GET_OFFER',
                                              'GET_OFFER_VIEW',
                                              'GET_PROMOTIONS',
                                              'GET_QUOTE',
                                              'GET_SHOWTIMES',
                                              'GET_STARTED',
                                              'INQUIRE_NOW',
                                              'INSTAGRAM_MESSAGE',
                                              'INSTALL_APP',
                                              'INSTALL_MOBILE_APP',
                                              'JOIN_CHANNEL',
                                              'LEARN_MORE',
                                              'LIKE_PAGE',
                                              'LISTEN_MUSIC',
                                              'LISTEN_NOW',
                                              'MAKE_AN_APPOINTMENT',
                                              'MESSAGE_PAGE',
                                              'MOBILE_DOWNLOAD',
                                              'NO_BUTTON',
                                              'OPEN_INSTANT_APP',
                                              'OPEN_LINK',
                                              'ORDER_NOW',
                                              'PAY_TO_ACCESS',
                                              'PLAY_GAME',
                                              'PLAY_GAME_ON_FACEBOOK',
                                              'PURCHASE_GIFT_CARDS',
                                              'RAISE_MONEY',
                                              'RECORD_NOW',
                                              'REFER_FRIENDS',
                                              'REQUEST_TIME',
                                              'SAY_THANKS',
                                              'SEE_MORE',
                                              'SELL_NOW',
                                              'SEND_A_GIFT',
                                              'SEND_GIFT_MONEY',
                                              'SEND_UPDATES',
                                              'SHARE',
                                              'SHOP_NOW',
                                              'SIGN_UP',
                                              'SOTTO_SUBSCRIBE',
                                              'START_ORDER',
                                              'SUBSCRIBE',
                                              'SWIPE_UP_PRODUCT',
                                              'SWIPE_UP_SHOP',
                                              'UPDATE_APP',
                                              'USE_APP',
                                              'USE_MOBILE_APP',
                                              'VIDEO_ANNOTATION',
                                              'VIDEO_CALL',
                                              'VIEW_CART',
                                              'VIEW_CHANNEL',
                                              'VIEW_INSTAGRAM_PROFILE',
                                              'VIEW_IN_CART',
                                              'VIEW_PRODUCT',
                                              'VISIT_PAGES_FEED',
                                              'WATCH_LIVE_VIDEO',
                                              'WATCH_MORE',
                                              'WATCH_VIDEO',
                                              'WHATSAPP_MESSAGE',
                                              'WOODHENGE_SUPPORT',
                                            ].map((option) => (
                                              <SelectItem key={option} value={option}>
                                                {option
                                                  .replace(/_/g, ' ')
                                                  .toLowerCase()
                                                  .replace(/\b\w/g, (c) => c.toUpperCase())}
                                              </SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <FormField
                                  control={boostPostForm.control}
                                  name="ad.callToActionUrl"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Destination URL</FormLabel>
                                      <FormControl>
                                        <Input placeholder="Creators promotional page" {...field} />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                        {/* Footer actions section */}
                        <div className="flex items-center justify-start gap-3 pt-4 border-t border-gray-200 mt-auto">
                          <Button variant="outline" onClick={onBoostPostCancel} className="w-24">
                            Cancel
                          </Button>
                          <Button className="w-24" type="submit">
                            Boost
                          </Button>
                        </div>
                      </form>
                    </Form>
                  )}
                </TabsContent>
              </Tabs>
            </SheetColumn>
          </SheetGrid>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default ContentPreviewSheetBoost;
